/** Consented product outcomes. The module does not load the SDK until both gates open. */
import type { PostHog } from 'posthog-js';
import { hasAnalyticsConsent } from '@/hooks/useConsent';
import { APPROVED_CAMPAIGN_LABELS } from '@/utils/campaignLabels';

/** Finite funnel labels. The four try_* milestones mark the anonymous demo and home_ready marks a loaded,
 * onboarded Home (not a saved card); none carries
 * content (words, photos, scores, language) so the sanitizer can enforce a closed set. */
const journeySteps = [
  'entry',
  'how_it_works',
  'try',
  'signup',
  'word',
  'photo',
  'card',
  'try_started',
  'try_photo_chosen',
  'try_completed',
  'try_signup_clicked',
  'home_ready',
] as const;
type JourneyStep = (typeof journeySteps)[number];

type ProductEvents = {
  $pageview: { $current_url: string; $pathname: string };
  campaign_visit: {
    source: string;
    medium: string;
    campaign: string;
    placement:
      | 'alphaandbetausers'
      | 'betatests'
      | 'betatesters'
      | 'testmyapp'
      | 'germanlearning_resources'
      | 'languagelearning_resources'
      | 'unknown';
    referrer_domain: string;
  };
  journey_step: { step: JourneyStep };
  // Email signups carry the original first-touch labels ONLY when the server claim returned an
  // A-side journey (the learner consented in both browsers); Google/other methods send method alone.
  signup_completed: {
    method: 'email' | 'google' | 'social' | 'unknown';
    source?: string;
    medium?: string;
    campaign?: string;
    placement?: string;
  };
  card_saved: {
    card_id: string;
    has_image: boolean;
    source_category: string;
    language_pair: string;
  };
  review_completed: { cards_reviewed: number };
  feedback_submitted: {
    submission_id: string;
    kind: 'rating' | 'message';
    rating: 'up' | 'down' | 'none';
  };
  session_entry: {
    state: 'anonymous' | 'signed_in';
    surface: 'public' | 'create' | 'library' | 'review' | 'other';
  };
  translation_outcome: { outcome: 'success' | 'error'; mode: 'automatic' | 'manual' };
  suggestion_outcome: { outcome: 'results' | 'empty' | 'error' };
  suggestion_chosen: { source: 'word_step' };
  photo_search_outcome: {
    outcome: 'results' | 'empty' | 'error' | 'cap';
    mode: 'automatic' | 'suggested' | 'custom' | 'source';
    source_category: string;
  };
  photo_selected: { source_category: string };
  ai_image_outcome: { outcome: 'success' | 'unavailable' | 'limit' | 'moderation' | 'duplicate' };
  ai_image_mode: { mode: 'opened' };
  card_save_failed: { mode: 'single' | 'batch' | 'draft' | 'edit'; reason: 'mirror' | 'other' };
  // Actual same-browser auth submissions/provider starts and their known terminal result. An
  // attempt or `success` here is never the verified `signup_completed` identity signal.
  auth_outcome: {
    action: 'signup' | 'login' | 'provider';
    method: 'email' | 'google';
    outcome: 'attempt' | 'success' | 'confirmation_required' | 'error' | 'cancelled';
  };
  // Persisting a learner's selected /try draft to their account. Never card_saved or activation.
  // complete = new durable rows; no_write = nothing new needed or written; retry/skipped = a
  // real user click on the retry screen.
  sample_import_outcome: {
    outcome:
      | 'attempt'
      | 'complete'
      | 'no_write'
      | 'partial'
      | 'failed'
      | 'interrupted'
      | 'retry'
      | 'skipped';
  };
};
type ProductEvent = keyof ProductEvents;

const token = import.meta.env.VITE_POSTHOG_PROJECT_TOKEN;
const enabled = import.meta.env.VITE_POSTHOG_ENABLED === 'true' && !!token;
const internalValidation = import.meta.env.VITE_POSTHOG_INTERNAL_VALIDATION === 'true';
const sentKey = 'vidi-posthog-sent';
const rejectedSignupKey = 'vidi-posthog-rejected-signup';
const signupKey = 'vidi-posthog-pending-signup';
const firstSeenKey = 'vidi-posthog-first-seen';
let clientPromise: Promise<PostHog | null> | null = null;
let clientInstance: PostHog | null = null;
let resetBarrier: Promise<void> = Promise.resolve();
// 'unknown' = verified session whose marker is absent or malformed. It differs from
// 'pending' (verification still running, finite queue allowed): nothing is queued
// and no SDK is loaded until a later verification proves the account external.
type AuthClassification = 'pending' | 'unknown' | 'anonymous' | 'external' | 'internal';
/** 'unknown' is what a missing or edited campaign label becomes; same convention as campaign_visit. */
const withUnknown = (labels: readonly string[]) => [...labels, 'unknown'];
/** Closed string sets, checked before the generic boolean/number copy would forward `true` or `42`. */
const closedValues: Partial<Record<ProductEvent, Record<string, readonly string[]>>> = {
  signup_completed: {
    method: ['email', 'google', 'social', 'unknown'],
    source: withUnknown(APPROVED_CAMPAIGN_LABELS.utm_source),
    medium: withUnknown(APPROVED_CAMPAIGN_LABELS.utm_medium),
    campaign: withUnknown(APPROVED_CAMPAIGN_LABELS.utm_campaign),
    placement: withUnknown(APPROVED_CAMPAIGN_LABELS.utm_content),
  },
  auth_outcome: {
    action: ['signup', 'login', 'provider'],
    method: ['email', 'google'],
    outcome: ['attempt', 'success', 'confirmation_required', 'error', 'cancelled'],
  },
  sample_import_outcome: {
    outcome: [
      'attempt',
      'complete',
      'no_write',
      'partial',
      'failed',
      'interrupted',
      'retry',
      'skipped',
    ],
  },
};
type QueuedEvent = {
  name: ProductEvent;
  properties: ProductEvents[ProductEvent];
  businessId?: string;
  boundUserId?: string;
  onCaptureComplete?: (captured: boolean) => void;
  /** Re-checked at flush: a queued stale auth terminal must not reach a later actor. */
  stillValid?: () => boolean;
};
let currentUserId: string | null = null;
let pendingAuthUserId: string | null = null;
let authClassification: AuthClassification = 'pending';
let authGeneration = 0;
let consentGeneration = 0;
// Bumps only when ownership of the page actually changes (actor A -> B, A -> signed out, signed
// out -> A, consent revoked, explicit logout). Same-actor reverification must NOT bump it. It is
// distinct from authGeneration, which bumps on every verification and would drop normal success.
let ownershipEpoch = 0;
// The last actor change, so an action's own anonymous -> returned-identity step is recognizable.
let lastOwnershipChange: { from: string | null; to: string | null } | null = null;
let identifiedUserId: string | null = null;
// The anonymous id THIS browser identified from, so a same-browser confirmation (which PostHog
// already merged through identify) never gets a redundant alias. Null when unknown.
let ownAnonymousId: string | null = null;
let queuedEvents: QueuedEvent[] = [];
// Stage uuids sent by this page, so a replay dedupes even when browser storage is denied.
const sentInMemory = new Set<string>();
// A login/signup/provider success is reported only once THIS account verifies as external,
// so internal/unknown accounts and a switched account never emit it.
let pendingAuthSuccess: {
  userId: string;
  stageId?: string;
  action: ProductEvents['auth_outcome']['action'];
  method: ProductEvents['auth_outcome']['method'];
  attempt?: AuthAttempt;
} | null = null;
let pageviewGeneration = 0;
let pageviewAttempt: { pathname: string; generation: number; sent: boolean } | null = null;
let pageviewRetryRequested = false;

export function isPosthogConfigured(): boolean {
  return enabled;
}

/** Safe setup diagnostic; never includes the token or account identifiers. */
export function posthogConfigurationIssue(): string | null {
  if (import.meta.env.VITE_POSTHOG_ENABLED !== 'true') return null;
  if (!token) return 'PostHog disabled: missing public project token';
  return null;
}

function canCapture(): boolean {
  return authClassification === 'anonymous' || authClassification === 'external';
}

function storageGet(key: string): string | null {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
}
function storageSet(key: string, value: string): void {
  try {
    sessionStorage.setItem(key, value);
  } catch {
    /* in-memory only */
  }
}
function storageRemove(key: string): void {
  try {
    sessionStorage.removeItem(key);
  } catch {
    /* storage denied */
  }
}
const safeValues: Record<ProductEvent, readonly string[]> = {
  $pageview: [],
  campaign_visit: ['source', 'medium', 'campaign', 'placement', 'referrer_domain'],
  journey_step: ['step'],
  signup_completed: ['method', 'source', 'medium', 'campaign', 'placement'],
  card_saved: ['card_id', 'has_image', 'source_category', 'language_pair'],
  review_completed: ['cards_reviewed'],
  feedback_submitted: ['submission_id', 'kind', 'rating'],
  session_entry: ['state', 'surface'],
  translation_outcome: ['outcome', 'mode'],
  suggestion_outcome: ['outcome'],
  suggestion_chosen: ['source'],
  photo_search_outcome: ['outcome', 'mode', 'source_category'],
  photo_selected: ['source_category'],
  ai_image_outcome: ['outcome'],
  ai_image_mode: ['mode'],
  card_save_failed: ['mode', 'reason'],
  auth_outcome: ['action', 'method', 'outcome'],
  sample_import_outcome: ['outcome'],
};
function cleanEventProperties(
  eventName: string,
  input: Record<string, unknown>,
): Record<string, unknown> {
  // PostHog enriches events from the request IP after client sanitization.
  // This per-event flag skips GeoIP enrichment; project settings must separately
  // discard the raw client IP from stored events.
  const output: Record<string, unknown> = { $geoip_disable: true };
  // These IDs link consented activity across signup. Drop URL, referrer, UTM,
  // profile and every other SDK default.
  for (const key of ['distinct_id', '$anon_distinct_id']) {
    const value = input[key];
    if (typeof value === 'string' && /^[a-zA-Z0-9_-]{1,100}$/.test(value)) output[key] = value;
  }
  for (const key of ['$is_identified', '$process_person_profile']) {
    const value = input[key];
    if (typeof value === 'boolean') output[key] = value;
  }
  // Keep only SDK-generated identifiers needed to group events into visits.
  for (const key of ['$session_id']) {
    const value = input[key];
    if (
      typeof value === 'string' &&
      /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value)
    )
      output[key] = value;
  }
  if (eventName === '$identify') {
    if (internalValidation) output.internal_validation = true;
    return output;
  }
  // Explicit, bounded SDK context for device-level funnel comparisons. Never
  // carry the raw user agent, location, referrer, session or profile defaults.
  for (const key of [
    '$browser',
    '$browser_version',
    '$os',
    '$os_version',
    '$device_type',
    '$lib',
    '$lib_version',
  ]) {
    const value = input[key];
    if (typeof value === 'string' && value.length <= 64 && /^[a-zA-Z0-9 ._-]+$/.test(value))
      output[key] = value;
  }
  for (const key of ['$viewport_width', '$viewport_height', '$screen_width', '$screen_height']) {
    const value = input[key];
    if (typeof value === 'number' && Number.isInteger(value) && value >= 0 && value <= 20000)
      output[key] = value;
  }
  const keys = safeValues[eventName as ProductEvent] || [];
  for (const key of keys) {
    const value = input[key];
    const closed = closedValues[eventName as ProductEvent]?.[key];
    if (closed) {
      if (typeof value === 'string' && closed.includes(value)) output[key] = value;
      continue;
    }
    if (typeof value === 'boolean' || (typeof value === 'number' && Number.isFinite(value)))
      output[key] = value;
    if (key === 'placement') {
      output[key] = [
        'alphaandbetausers',
        'betatests',
        'betatesters',
        'testmyapp',
        'germanlearning_resources',
        'languagelearning_resources',
      ].includes(String(value))
        ? value
        : 'unknown';
    } else if (typeof value === 'string' && /^[a-zA-Z0-9_.-]{1,100}$/.test(value))
      output[key] = value;
  }
  if (eventName === '$pageview') {
    const pathname = canonicalPagePath(input.$pathname);
    if (pathname) {
      output.$pathname = pathname;
      output.$current_url = 'https://vidilearn.com' + pathname;
    }
  }
  if (internalValidation) output.internal_validation = true;
  return output;
}

/**
 * The ONE alias the app may send: A's anonymous id as an alias of the verified account, armed by
 * completeReceiptedSignup immediately around its own client.alias() call and consumed by the first
 * `$create_alias` envelope the SDK produces. Every other alias, including one that arrives while
 * armed with different ids, is dropped like any unlisted event.
 */
let aliasGuard: { userId: string; anonymousId: string } | null = null;

function sanitizeAliasEnvelope<T extends { event: string; properties?: Record<string, unknown> }>(
  event: T,
): T | null {
  const guard = aliasGuard;
  // One shot even on a mismatch: an unsolicited alias that gets here first burns the guard, so our
  // own alias is then dropped (fail closed) instead of a second envelope slipping through.
  aliasGuard = null;
  const properties = event.properties;
  if (
    !guard ||
    !properties ||
    properties.token !== token ||
    properties.alias !== guard.anonymousId ||
    properties.distinct_id !== guard.userId
  )
    return null;
  // Exactly four fields (plus the internal-validation marker used by the synthetic fixture):
  // no URL, referrer, UTM, session, device or profile default can ride on an alias.
  // Direction: the account is the canonical distinct_id and A's never-identified anonymous id is the
  // alias_id. PostHog forbids an id that was already an identify() target (the account) as an alias_id.
  event.properties = {
    alias: guard.anonymousId,
    distinct_id: guard.userId,
    token,
    $geoip_disable: true,
    ...(internalValidation ? { internal_validation: true } : {}),
  };
  const envelope = event as Record<string, unknown>;
  delete envelope.$set;
  delete envelope.$set_once;
  delete envelope.$unset;
  return event;
}

export function sanitizePosthogEnvelope<
  T extends { event: string; properties?: Record<string, unknown> },
>(event: T | null): T | null {
  if (event?.event === '$create_alias') return sanitizeAliasEnvelope(event);
  if (
    !event ||
    (event.event !== '$identify' &&
      !Object.prototype.hasOwnProperty.call(allowedEvents, event.event))
  )
    return null;
  const receivedToken = event.properties?.token;
  if (receivedToken !== token) return null;
  if (event.event === '$pageview' && !canonicalPagePath(event.properties?.$pathname)) return null;
  if (
    event.event === 'journey_step' &&
    !journeySteps.includes(event.properties?.step as JourneyStep)
  )
    return null;
  event.properties = { ...cleanEventProperties(event.event, event.properties || {}), token };
  const envelope = event as Record<string, unknown>;
  delete envelope.$set;
  delete envelope.$set_once;
  delete envelope.$unset;
  return event;
}

async function getClient(): Promise<PostHog | null> {
  if (!enabled || !hasAnalyticsConsent() || !canCapture()) return null;
  const generation = consentGeneration;
  const authAtCall = authGeneration;
  await resetBarrier;
  if (generation !== consentGeneration || authAtCall !== authGeneration || !canCapture())
    return null;
  if (!clientPromise) {
    clientPromise = import('posthog-js')
      .then(({ default: posthog }) => {
        if (
          !hasAnalyticsConsent() ||
          generation !== consentGeneration ||
          authAtCall !== authGeneration ||
          !canCapture()
        )
          return null;
        posthog.init(token, {
          api_host: 'https://us.i.posthog.com',
          ui_host: 'https://us.posthog.com',
          autocapture: false,
          capture_pageview: false,
          request_batching: false,
          capture_pageleave: false,
          capture_dead_clicks: false,
          capture_exceptions: false,
          capture_performance: false,
          capture_heatmaps: false,
          disable_session_recording: true,
          disable_surveys: true,
          advanced_disable_flags: true,
          opt_out_capturing_by_default: true,
          opt_out_persistence_by_default: true,
          person_profiles: 'identified_only',
          save_referrer: false,
          save_campaign_params: false,
          mask_all_text: true,
          mask_all_element_attributes: true,
          before_send: sanitizePosthogEnvelope,
        });
        posthog.opt_in_capturing({ captureEventName: false });
        clientInstance = posthog;
        return posthog;
      })
      .catch(() => null);
  }
  const client = await clientPromise;
  if (!client && authAtCall === authGeneration) clientPromise = null;
  if (
    !client ||
    !hasAnalyticsConsent() ||
    generation !== consentGeneration ||
    authAtCall !== authGeneration ||
    !canCapture()
  )
    return null;
  try {
    if (!client.has_opted_in_capturing()) client.opt_in_capturing({ captureEventName: false });
    clientInstance = client;
    return client;
  } catch {
    return null;
  }
}

const allowedEvents: Record<ProductEvent, true> = {
  $pageview: true,
  campaign_visit: true,
  journey_step: true,
  signup_completed: true,
  card_saved: true,
  review_completed: true,
  feedback_submitted: true,
  session_entry: true,
  translation_outcome: true,
  suggestion_outcome: true,
  suggestion_chosen: true,
  photo_search_outcome: true,
  photo_selected: true,
  ai_image_outcome: true,
  ai_image_mode: true,
  card_save_failed: true,
  auth_outcome: true,
  sample_import_outcome: true,
};

function stopClientImmediately(): void {
  try {
    clientInstance?.opt_out_capturing();
  } catch {
    /* Analytics failure must not interrupt consent or learning. */
  }
}

function scheduleClientReset(previousClient: Promise<PostHog | null> | null): void {
  resetBarrier = resetBarrier
    .then(async () => {
      const client = await previousClient;
      if (!client) return;
      client.reset(true);
      client.opt_out_capturing();
    })
    .catch(() => {});
}

/** Revocation removes this adapter's keys and resets PostHog's identity/persistence. */
export function syncPosthogConsent(granted: boolean): void {
  if (!enabled) return;
  if (granted) {
    if (canCapture()) void getClient();
    return;
  }
  consentGeneration += 1;
  pageviewGeneration += 1;
  pageviewAttempt = null;
  pageviewRetryRequested = false;
  authGeneration += 1;
  authClassification = 'pending';
  pendingAuthUserId = null;
  pendingAuthSuccess = null;
  ownershipEpoch += 1;
  lastOwnershipChange = null;
  currentUserId = null;
  identifiedUserId = ownAnonymousId = null;
  queuedEvents.forEach((event) => event.onCaptureComplete?.(false));
  queuedEvents = [];
  storageRemove(sentKey);
  storageRemove(signupKey);
  storageRemove(rejectedSignupKey);
  try {
    localStorage.removeItem(firstSeenKey);
  } catch {
    /* storage denied */
  }
  const previousClient = clientPromise;
  stopClientImmediately();
  clientPromise = null;
  clientInstance = null;
  scheduleClientReset(previousClient);
}

/** Start a fresh server verification before any event can leave this account state. */
export function beginPosthogAuthVerification(userId: string | null): number {
  if (!enabled) return authGeneration;
  const previous = authClassification;
  const previousUser = currentUserId;
  const previousActor = currentUserId ?? pendingAuthUserId;
  const priorPromise = clientPromise;
  if (previousActor !== userId) {
    ownershipEpoch += 1;
    lastOwnershipChange = { from: previousActor, to: userId };
  }
  authGeneration += 1;
  authClassification = 'pending';
  pendingAuthUserId = userId;
  if (pendingAuthSuccess && pendingAuthSuccess.userId !== userId) pendingAuthSuccess = null;
  currentUserId = null;
  if (previous !== 'pending') {
    queuedEvents.forEach((event) => event.onCaptureComplete?.(false));
    queuedEvents = [];
  }
  if (!clientInstance) clientPromise = null;
  if (previousUser && previousUser !== userId) storageRemove(signupKey);
  if (previous === 'external' || previous === 'internal') {
    stopClientImmediately();
    identifiedUserId = ownAnonymousId = null;
    clientInstance = null;
    scheduleClientReset(priorPromise);
  }
  return authGeneration;
}

/** Called only after AuthContext has a definitive no-session state. */
export function setAnonymousAnalyticsUser(ticket: number): void {
  if (!enabled || !hasAnalyticsConsent() || ticket !== authGeneration || pendingAuthUserId) return;
  authClassification = 'anonymous';
  currentUserId = null;
  void flushQueuedEvents();
}

/**
 * Pass only an id and the RAW app_metadata marker from a matching server getUser()
 * result. Strictly JSON `false` is external and `true` is internal; a missing or
 * malformed marker is unknown and captures nothing.
 */
export function setVerifiedAnalyticsUser(userId: string, marker: unknown, ticket: number): void {
  if (
    !enabled ||
    !hasAnalyticsConsent() ||
    ticket !== authGeneration ||
    pendingAuthUserId !== userId
  )
    return;
  pendingAuthUserId = null;
  currentUserId = userId;
  if (marker !== false) {
    authClassification = marker === true ? 'internal' : 'unknown';
    stopClientImmediately();
    identifiedUserId = ownAnonymousId = null;
    queuedEvents.forEach((event) => event.onCaptureComplete?.(false));
    queuedEvents = [];
    pendingAuthSuccess = null;
    const priorClient = clientPromise;
    scheduleClientReset(priorClient);
    return;
  }
  authClassification = 'external';
  void flushQueuedEvents();
  if (pendingAuthSuccess?.userId === userId) {
    const { action, method, stageId, attempt } = pendingAuthSuccess;
    pendingAuthSuccess = null;
    captureProductEvent(
      'auth_outcome',
      { action, method, outcome: 'success' },
      stageId,
      userId,
      undefined,
      attempt ? () => attemptStillOwns(attempt, userId) : undefined,
    );
  }
}

async function flushQueuedEvents(): Promise<void> {
  const waiting = queuedEvents;
  queuedEvents = [];
  for (const event of waiting) {
    if (
      (event.boundUserId && event.boundUserId !== currentUserId) ||
      (event.stillValid && !event.stillValid())
    ) {
      event.onCaptureComplete?.(false);
      continue;
    }
    captureProductEvent(
      event.name,
      event.properties,
      event.businessId,
      event.boundUserId,
      event.onCaptureComplete,
      event.stillValid,
    );
  }
}

/** Keep the first consented landing date through an email confirmation reload/new tab. */
export function noteConsentedFirstSeen(firstSeenAt: string): void {
  if (!enabled || !hasAnalyticsConsent() || !Number.isFinite(Date.parse(firstSeenAt))) return;
  try {
    if (!localStorage.getItem(firstSeenKey)) localStorage.setItem(firstSeenKey, firstSeenAt);
  } catch {
    /* analytics may be incomplete when storage is unavailable */
  }
}

/** An edge signup response is only a candidate: duplicate email may return a fake user. */
export function noteSignupCandidate(userId: string | undefined): void {
  if (!enabled || !hasAnalyticsConsent() || !userId) return;
  storageRemove(rejectedSignupKey);
  storageSet(signupKey, userId);
}

/** Call after a server-verified auth user is available. Unknown signups stay unknown. */
export function completeVerifiedSignup(
  userId: string,
  createdAt: string,
  verifiedSoleGoogle = false,
  /** Server-verified sole email provider: such an account is counted by the receipt claim instead. */
  soleEmailAccount = false,
): void {
  if (!enabled || !hasAnalyticsConsent()) return;
  // Wait for the current external verification so an unknown or pending account
  // does not consume the candidate before it can be counted.
  if (authClassification !== 'external' || currentUserId !== userId) return;
  const candidate = storageGet(signupKey);
  let firstSeenAt: string | null = null;
  try {
    firstSeenAt = localStorage.getItem(firstSeenKey);
  } catch {
    /* no storage */
  }
  const accountCreated = Date.parse(createdAt);
  const landingSeen = firstSeenAt ? Date.parse(firstSeenAt) : NaN;
  if (candidate && candidate !== userId) {
    storageRemove(signupKey);
    storageSet(rejectedSignupKey, '1');
    return;
  }
  if (storageGet(rejectedSignupKey)) return;
  // A server-created account after this consented journey is the new-account fact.
  // A duplicate signup can return a fake ID, but it cannot authenticate as that ID.
  if (
    !Number.isFinite(accountCreated) ||
    !Number.isFinite(landingSeen) ||
    accountCreated < landingSeen
  )
    return;
  // Email signups are counted ONLY by the receipt claim (completeReceiptedSignup): a browser clock
  // and a sessionStorage candidate are not proof, and counting here too would double count.
  // Exact scope of the intentional change (everything else keeps its previous method):
  //   email candidate, not Google          -> was 'email', now nothing (the claim counts it)
  //   sole-email account, no candidate     -> was 'unknown', now nothing (the claim counts it)
  //   Google + email candidate (mixed)     -> still 'unknown' (ambiguity stays visible, never 'google')
  //   sole verified Google, no candidate   -> still 'google'
  //   any other account, no candidate      -> still 'unknown'
  // The candidate is consumed on use exactly as before.
  const method = candidate
    ? verifiedSoleGoogle
      ? 'unknown'
      : null
    : verifiedSoleGoogle
      ? 'google'
      : soleEmailAccount
        ? null
        : 'unknown';
  if (candidate) storageRemove(signupKey);
  if (method) captureProductEvent('signup_completed', { method }, userId);
}

/**
 * The current analytics consent period. It changes whenever consent is withdrawn, so a value read
 * before an awaited request tells the caller afterwards whether consent was withdrawn in between
 * (even if it was granted again), without exposing any analytics state.
 */
export function currentConsentPeriod(): number {
  return consentGeneration;
}

/** Snapshot for a pending optional signup context; every actor change invalidates it. */
export function currentAnalyticsOwnershipPeriod(): number {
  return ownershipEpoch;
}

/** Identify once per verified account and remember which anonymous id this browser identified from. */
function identifyOnce(client: PostHog, userId: string): void {
  if (identifiedUserId === userId) return;
  // Reading the prior id is bookkeeping only. It must never stop the identify or the event that
  // follows (a narrower SDK handle, or a throwing getter, just leaves the same-browser check unknown).
  // The state is read BEFORE identify (identify itself flips it). Only an explicit 'anonymous' is
  // evidence that `before` was this browser's own anonymous id; undefined (an SDK that does not
  // report it) is unknown, never treated as anonymous.
  let before: string | null = null;
  let wasAnonymous = false;
  try {
    before = client.get_distinct_id?.() ?? null;
    wasAnonymous = client.get_property?.('$user_state') === 'anonymous';
  } catch {
    before = null;
    wasAnonymous = false;
  }
  // Cleared on every real identify attempt, so a stale id from an earlier account never survives.
  ownAnonymousId = null;
  client.identify(userId);
  identifiedUserId = userId;
  if (before && before !== userId && wasAnonymous) ownAnonymousId = before;
}

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

/**
 * Browser A, at signup time: this browser's PostHog anonymous id, or null. Strictly synchronous and
 * optional, never waits for analytics: null unless consent is granted, the SDK is already loaded,
 * this page is anonymous (no account, none being verified, never identified) and capture is opted
 * in. Essential Only and a not-yet-ready SDK both return null, so the signup request omits the field.
 */
export function readAnonymousJourneyId(): string | null {
  try {
    const client = clientInstance;
    if (
      !enabled ||
      !hasAnalyticsConsent() ||
      !client ||
      authClassification !== 'anonymous' ||
      currentUserId ||
      pendingAuthUserId ||
      identifiedUserId ||
      !client.has_opted_in_capturing() ||
      // Only an explicit 'anonymous' qualifies: an unreported state is unknown, not anonymous.
      client.get_property('$user_state') !== 'anonymous'
    )
      return null;
    const id = client.get_distinct_id();
    return typeof id === 'string' && UUID_PATTERN.test(id) ? id.toLowerCase() : null;
  } catch {
    return null;
  }
}

/** What the server claim returned. `journey` exists only when browser A consented and did not revoke. */
export type ReceiptedSignupClaim = Readonly<{
  claimId: string;
  journey: Readonly<{
    anonymousId: string;
    source: string;
    medium: string;
    campaign: string;
    placement: string;
  }> | null;
}>;

/** Snapshot of the actor and consent period that is allowed to spend a signup claim. */
export type ReceiptedSignupTicket = Readonly<{
  userId: string;
  authGeneration: number;
  consentGeneration: number;
  ownershipEpoch: number;
}>;

/**
 * Browser B, BEFORE the claim request: a ticket only for a consented, server-verified external
 * account. Null means do not call the claim at all (Essential Only, internal/unknown account,
 * verification still running), so the one-time claim is never spent where nothing could be sent.
 */
export function beginReceiptedSignupClaim(userId: string): ReceiptedSignupTicket | null {
  if (
    !enabled ||
    !hasAnalyticsConsent() ||
    authClassification !== 'external' ||
    currentUserId !== userId
  )
    return null;
  return { userId, authGeneration, consentGeneration, ownershipEpoch };
}

export type ReceiptedSignupOutcome = Readonly<{
  /** The signup event was handed to the SDK (queued). NOT proof that PostHog ingested it. */
  signupQueued: boolean;
  /** The alias was handed to the SDK. NOT proof that PostHog merged anything. */
  aliasQueued: boolean;
}>;

/**
 * Browser B, AFTER a successful claim for exactly this account. The only email path to
 * `signup_completed`. Order is fixed: identify (normal SDK flow, no reset to A), one guarded alias
 * of A's anonymous id into the account, then one `signup_completed` with the claim UUID as event id.
 *
 * Every state check runs again after the one awaited boundary (getClient); from there to the capture
 * everything is synchronous, so the actor cannot change in between. Never throws, never blocks
 * anything: a false outcome is a lost optional analytics event, not an error. Rejected on purpose:
 * resetting B to A's identity (would overwrite B's own SDK identity) and any identified-to-identified
 * merge (alias is skipped unless this browser was anonymous until it identified as the account).
 */
export async function completeReceiptedSignup(
  ticket: ReceiptedSignupTicket,
  claim: ReceiptedSignupClaim,
): Promise<ReceiptedSignupOutcome> {
  const none: ReceiptedSignupOutcome = { signupQueued: false, aliasQueued: false };
  const stillOwns = () =>
    enabled &&
    hasAnalyticsConsent() &&
    ticket.consentGeneration === consentGeneration &&
    ticket.authGeneration === authGeneration &&
    ticket.ownershipEpoch === ownershipEpoch &&
    authClassification === 'external' &&
    currentUserId === ticket.userId;
  const dedupe = `signup_completed:${claim.claimId}`;
  try {
    if (!stillOwns() || sentInMemory.has(dedupe)) return none;
    const client = await getClient();
    if (!client || !stillOwns()) return none;
    identifyOnce(client, ticket.userId);
    // The verified account must be this SDK's identity before anything links to it.
    if (client.get_distinct_id() !== ticket.userId) return none;
    let aliasQueued = false;
    const journey = claim.journey;
    if (
      journey &&
      journey.anonymousId !== ticket.userId &&
      // Alias only when THIS browser was anonymous until it identified as the account. A browser
      // that was already identified (or is A itself, whose own identify already merged its id)
      // gets no alias, so identified-to-identified merges and redundant aliases never happen.
      ownAnonymousId !== null &&
      ownAnonymousId !== journey.anonymousId &&
      client.get_property('$device_id') !== journey.anonymousId
    ) {
      aliasGuard = { userId: ticket.userId, anonymousId: journey.anonymousId };
      try {
        // alias(alias, original) sends {alias, distinct_id: original}. Here alias = A's anonymous id
        // and original = the account that identify() just made canonical, so the account is never
        // used as an alias_id (PostHog forbids that after identify). A void result is a sanitizer
        // drop or disabled person processing; -1/-2 are SDK refusals. None is success, and even a
        // returned envelope only means "queued", never "ingested" or "merged".
        const result = client.alias(journey.anonymousId, ticket.userId);
        aliasQueued =
          typeof result === 'object' && result !== null && result.event === '$create_alias';
      } catch {
        aliasQueued = false;
      } finally {
        aliasGuard = null;
      }
    }
    // The alias call is synchronous, but re-check anyway: cheap, and no await lies between here and
    // the capture so the verdict cannot go stale.
    if (!stillOwns() || client.get_distinct_id() !== ticket.userId)
      return { signupQueued: false, aliasQueued };
    sentInMemory.add(dedupe);
    const properties: ProductEvents['signup_completed'] = {
      method: 'email',
      ...(journey
        ? {
            source: journey.source,
            medium: journey.medium,
            campaign: journey.campaign,
            placement: journey.placement,
          }
        : {}),
    };
    const result = client.capture('signup_completed', properties, { uuid: claim.claimId });
    return { signupQueued: typeof result === 'object' && result !== null, aliasQueued };
  } catch {
    aliasGuard = null;
    return none;
  }
}

/** `businessId` is a stable UUID for outcomes that can be retried or remounted. */
export function captureProductEvent<E extends ProductEvent>(
  name: E,
  properties: ProductEvents[E],
  businessId?: string,
  boundUserId?: string,
  onCaptureComplete?: (captured: boolean) => void,
  stillValid?: () => boolean,
): void {
  let completed = false;
  const finish = (captured: boolean) => {
    if (completed) return;
    completed = true;
    onCaptureComplete?.(captured);
  };
  if (!enabled || !hasAnalyticsConsent()) {
    finish(false);
    return;
  }
  if (authClassification === 'internal' || (stillValid && !stillValid())) {
    finish(false);
    return;
  }
  if (authClassification === 'pending') {
    const queueable =
      name === '$pageview' ||
      name === 'campaign_visit' ||
      name === 'journey_step' ||
      // Anonymous-side auth steps (attempt/error/callback cancel) carry no account.
      name === 'auth_outcome' ||
      ((name === 'card_saved' || name === 'sample_import_outcome') &&
        !!boundUserId &&
        boundUserId === pendingAuthUserId);
    if (queueable && queuedEvents.length < 30) {
      queuedEvents.push({
        name,
        properties,
        businessId,
        boundUserId,
        onCaptureComplete,
        stillValid,
      });
    } else {
      finish(false);
    }
    return;
  }
  if (boundUserId && boundUserId !== currentUserId) {
    finish(false);
    return;
  }
  const userAtCall = currentUserId;
  const consentAtCall = consentGeneration;
  const authAtCall = authGeneration;
  const dedupe = businessId ? `${name}:${businessId}` : null;
  if (dedupe && (sentInMemory.has(dedupe) || storageGet(sentKey)?.split('|').includes(dedupe))) {
    finish(false);
    return;
  }
  void getClient()
    .then((client) => {
      if (
        !client ||
        consentAtCall !== consentGeneration ||
        authAtCall !== authGeneration ||
        userAtCall !== currentUserId ||
        !canCapture() ||
        (stillValid && !stillValid())
      ) {
        finish(false);
        return;
      }
      if (dedupe) {
        if (sentInMemory.has(dedupe)) {
          finish(false);
          return;
        }
        sentInMemory.add(dedupe);
        const recent = (storageGet(sentKey) || '').split('|').filter(Boolean).slice(-39);
        if (recent.includes(dedupe)) {
          finish(false);
          return;
        }
        storageSet(sentKey, [...recent, dedupe].join('|'));
      }
      try {
        if (userAtCall) identifyOnce(client, userAtCall);
        client.capture(name, properties, businessId ? { uuid: businessId } : undefined);
        finish(true);
      } catch {
        finish(false);
        /* learning and feedback must survive blocked analytics. */
      }
    })
    .catch(() => {
      finish(false);
      /* SDK/storage failures cannot reject a product action. */
    });
}

const canonicalPagePaths = new Set([
  '/',
  '/about',
  '/achievements',
  '/changelog',
  '/contact',
  '/create',
  '/create-sketch',
  '/ear-training',
  '/faq',
  '/features',
  '/feedback',
  '/forgot-password',
  '/founding-feedback',
  '/guides',
  '/how-it-works',
  '/home',
  '/impressum',
  '/library',
  '/login',
  '/onboarding',
  '/pricing',
  '/privacy',
  '/reset-password',
  '/review',
  '/settings',
  '/signup',
  '/stats',
  '/terms',
  '/tutor',
  '/try',
  '/word-pack',
]);

function canonicalPagePath(pathname: unknown): string | null {
  if (typeof pathname !== 'string' || pathname.includes('?') || pathname.includes('#')) return null;
  const normalized = pathname.replace(/\/+$/, '') || '/';
  if (canonicalPagePaths.has(normalized)) return normalized;
  if (
    /^\/review\/[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
      normalized,
    )
  )
    return '/review';
  return null;
}

/** Capture one consented pageview per canonical route, with no URL-derived content. */
export function captureConsentedPageview(pathname: string): void {
  if (!enabled || !hasAnalyticsConsent() || authClassification === 'internal') return;
  const canonicalPath = canonicalPagePath(pathname);
  if (!canonicalPath) return;
  if (pageviewAttempt?.pathname === canonicalPath) {
    if (!pageviewAttempt.sent) pageviewRetryRequested = true;
    return;
  }
  const generation = ++pageviewGeneration;
  pageviewAttempt = { pathname: canonicalPath, generation, sent: false };
  pageviewRetryRequested = false;
  captureProductEvent(
    '$pageview',
    {
      $current_url: 'https://vidilearn.com' + canonicalPath,
      $pathname: canonicalPath,
    },
    undefined,
    undefined,
    (captured) => {
      if (pageviewAttempt?.generation !== generation) return;
      if (captured) {
        pageviewAttempt.sent = true;
        pageviewRetryRequested = false;
        return;
      }
      pageviewAttempt = null;
      if (pageviewRetryRequested) {
        pageviewRetryRequested = false;
        captureConsentedPageview(canonicalPath);
      }
    },
  );
}

/** Snapshot the verified actor before an asynchronous product request starts. */
export type ProductOutcomeActor = Readonly<{
  userId: string | null;
  authGeneration: number;
  consentGeneration: number;
}>;

export function beginProductOutcome(): ProductOutcomeActor | null {
  if (!enabled || !hasAnalyticsConsent() || !canCapture()) return null;
  return { userId: currentUserId, authGeneration, consentGeneration };
}

export function captureProductOutcome<E extends ProductEvent>(
  actor: ProductOutcomeActor | null,
  name: E,
  properties: ProductEvents[E],
  businessId?: string,
): void {
  if (
    !actor ||
    actor.authGeneration !== authGeneration ||
    actor.consentGeneration !== consentGeneration ||
    actor.userId !== currentUserId ||
    !canCapture()
  )
    return;
  captureProductEvent(name, properties, businessId, actor.userId ?? undefined);
}

/**
 * Snapshot taken BEFORE an auth request is awaited. It binds a terminal (error, confirmation,
 * returned-session success) to the consent period and the ownership epoch that started the
 * request. Opaque by design: consent, epoch and actor never become event properties, and the
 * stage UUIDs stay dedupe-only. `authGeneration` is deliberately NOT part of it: a normal login
 * moves anonymous -> own account through setSession, so it would drop every success.
 * `stale` is set by gateAuthAttempt when ownership already moved before the session was adopted.
 */
export type AuthAttempt = {
  readonly consentGeneration: number;
  readonly epoch: number;
  readonly actor: string | null;
  stale: boolean;
};

/** The account this page is acting as right now: verified, else the one being verified. */
const currentAuthActor = (): string | null => currentUserId ?? pendingAuthUserId;

/** Null without consent: an action started unmeasured is never measured after a later grant. */
export function beginAuthAttempt(): AuthAttempt | null {
  try {
    if (!enabled || !hasAnalyticsConsent()) return null;
    return {
      consentGeneration,
      epoch: ownershipEpoch,
      actor: currentAuthActor(),
      stale: false,
    };
  } catch {
    return null;
  }
}

/**
 * Call right BEFORE adopting a returned session. Ownership that already moved (resurrection
 * A -> logout -> A, anonymous -> B -> anonymous, consent revoke) makes the attempt unmeasured
 * for good; the auth action itself is unaffected. Returns null when stale.
 */
export function gateAuthAttempt(attempt: AuthAttempt | null | undefined): AuthAttempt | null {
  try {
    if (!attempt || attempt.stale) return null;
    if (attempt.consentGeneration !== consentGeneration || attempt.epoch !== ownershipEpoch) {
      attempt.stale = true;
      return null;
    }
    return attempt;
  } catch {
    return null;
  }
}

/** An explicit logout is an ownership change even when the anonymous actor stays null. */
export function noteAuthLogout(): void {
  ownershipEpoch += 1;
  lastOwnershipChange = null;
}

/**
 * Same consent period and ownership epoch. A returned session (`returnedUserId`) may add exactly
 * ONE ownership change: the attempt's own actor -> the returned identity (the auth listener
 * adopting the session). Any other change, including a second one, means the request is stale.
 * Terminal errors/confirmations allow no ownership change.
 */
function attemptStillOwns(attempt: AuthAttempt, returnedUserId?: string): boolean {
  if (attempt.stale || attempt.consentGeneration !== consentGeneration) return false;
  if (attempt.epoch === ownershipEpoch) return true;
  return (
    returnedUserId !== undefined &&
    ownershipEpoch === attempt.epoch + 1 &&
    lastOwnershipChange?.from === attempt.actor &&
    lastOwnershipChange?.to === returnedUserId
  );
}

/**
 * Anonymous-side auth step (submission, provider start, returned error, callback cancel).
 * `stageId` is one UUID per submission stage; a retry passes new ones. Never throws.
 * `attempt`: undefined = no attempt context (callback return); null = the request started
 * without consent, so it is dropped; otherwise it must still own the consent period and actor.
 */
export function captureAuthOutcome(
  action: ProductEvents['auth_outcome']['action'],
  method: ProductEvents['auth_outcome']['method'],
  outcome: Exclude<ProductEvents['auth_outcome']['outcome'], 'success'>,
  stageId?: string,
  attempt?: AuthAttempt | null,
): void {
  try {
    if (attempt === null || (attempt && !attemptStillOwns(attempt))) return;
    captureProductEvent(
      'auth_outcome',
      { action, method, outcome },
      stageId,
      undefined,
      undefined,
      attempt ? () => attemptStillOwns(attempt) : undefined,
    );
  } catch {
    /* analytics bookkeeping can never fail an auth flow */
  }
}

/**
 * A returned auth success, reported only for an account verified as external: sent now when
 * that verification already finished, held until it finishes otherwise, and dropped (fail
 * closed) for an internal/unknown account or a stale attempt. Never throws.
 */
export function noteAuthSuccess(
  userId: string,
  action: ProductEvents['auth_outcome']['action'],
  method: ProductEvents['auth_outcome']['method'],
  stageId?: string,
  attempt?: AuthAttempt | null,
): void {
  try {
    if (!enabled || !hasAnalyticsConsent() || !userId) return;
    if (attempt === null || (attempt && !attemptStillOwns(attempt, userId))) return;
    if (currentUserId === userId) {
      if (authClassification === 'external') {
        captureProductEvent(
          'auth_outcome',
          { action, method, outcome: 'success' },
          stageId,
          userId,
          undefined,
          attempt ? () => attemptStillOwns(attempt, userId) : undefined,
        );
        return;
      }
      if (authClassification === 'internal' || authClassification === 'unknown') return;
    }
    pendingAuthSuccess = { userId, stageId, action, method, attempt: attempt ?? undefined };
  } catch {
    /* analytics bookkeeping can never fail an auth flow */
  }
}

/** Read when an import stage starts; a later consent change makes its results stale. */
export function sampleImportConsentToken(): number {
  return consentGeneration;
}

/**
 * One import stage for a selected /try draft, bound to the account that started it.
 * `stageId` is a UUID unique per stage so replays dedupe but a user retry is a new stage.
 * A result from before a consent change (token mismatch) is discarded. Never throws.
 */
export function captureSampleImportOutcome(
  userId: string,
  outcome: ProductEvents['sample_import_outcome']['outcome'],
  stageId: string,
  consentToken?: number,
): void {
  try {
    if (consentToken !== undefined && consentToken !== consentGeneration) return;
    captureProductEvent('sample_import_outcome', { outcome }, stageId, userId);
  } catch {
    /* analytics bookkeeping can never fail an import */
  }
}
